import { useEffect, useState } from 'react';
import { apiSaveConsent, type ConsentSettings, type MarketingSettingsView } from '~/lib/api/marketing';
import { classNames } from '~/utils/classNames';
import {
  CARD,
  CAPTION,
  ErrorBanner,
  Field,
  Section,
  Segmented,
  Select,
  Spinner,
  Switch,
} from '~/components/workbench/tools/payments/ui';
import {
  apiErrorMessage,
  CONSENT_DEFAULT_MESSAGE,
  CONSENT_DEFAULT_TITLE,
  CONSENT_POSITIONS,
  consentModeHint,
  consentPreview,
  isValidPrivacyUrl,
} from './marketing-model';

type Position = NonNullable<ConsentSettings['position']>;

interface Draft {
  enabled: boolean;
  mode: ConsentSettings['mode'];
  title: string;
  message: string;
  privacyUrl: string;
  position: Position;
}

function draftFrom(c: ConsentSettings): Draft {
  return {
    enabled: c.enabled,
    mode: c.mode,
    title: c.title ?? '',
    message: c.message ?? '',
    privacyUrl: c.privacyUrl ?? '',
    position: c.position ?? 'bottom',
  };
}

function toSettings(d: Draft): ConsentSettings {
  return {
    enabled: d.enabled,
    mode: d.mode,
    ...(d.title.trim() ? { title: d.title.trim() } : {}),
    ...(d.message.trim() ? { message: d.message.trim() } : {}),
    ...(d.privacyUrl.trim() ? { privacyUrl: d.privacyUrl.trim() } : {}),
    position: d.position,
  };
}

export function ConsentView({
  appId,
  view,
  onSaved,
}: {
  appId: string;
  view: MarketingSettingsView;
  onSaved: (consent: ConsentSettings) => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftFrom(view.consent));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => setDraft(draftFrom(view.consent)), [view.consent]);

  const dirty = JSON.stringify(toSettings(draft)) !== JSON.stringify(toSettings(draftFrom(view.consent)));
  const urlOk = isValidPrivacyUrl(draft.privacyUrl);
  const preview = consentPreview(draft);

  const update = (patch: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setSaved(false);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();

    // Saving unchanged defaults is allowed: it turns the banner on explicitly.
    if (!urlOk) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      onSaved(await apiSaveConsent(appId, toSettings(draft)));
      setSaved(true);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the consent banner.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Cookie consent" icon="i-ph:cookie">
      <form onSubmit={save} className={classNames(CARD, 'space-y-3.5 p-4')}>
        <div className="space-y-1.5">
          <Switch checked={draft.enabled} onChange={(enabled) => update({ enabled })} label="Show a consent banner" />
          <p className="text-[11px] leading-relaxed text-muted">
            {consentModeHint(draft.enabled, draft.mode)}
            {draft.enabled && ' It appears on your published site once a tag is on, or after you save it here.'}
          </p>
        </div>

        {draft.enabled && (
          <>
            <div className="space-y-1.5">
              <span className="block text-[11px] text-secondary">Mode</span>
              <Segmented
                label="Consent mode"
                value={draft.mode}
                options={[
                  ['opt-in', 'Opt-in (recommended)'],
                  ['notice', 'Notice'],
                ]}
                onChange={(mode) => update({ mode })}
              />
            </div>

            <Field label="Title">
              <input
                value={draft.title}
                onChange={(e) => update({ title: e.target.value })}
                maxLength={120}
                placeholder={CONSENT_DEFAULT_TITLE}
                className="ui-input"
              />
            </Field>

            <Field label="Message">
              <textarea
                value={draft.message}
                onChange={(e) => update({ message: e.target.value })}
                maxLength={600}
                rows={3}
                placeholder={CONSENT_DEFAULT_MESSAGE}
                className="ui-input resize-y"
              />
            </Field>

            <Field
              label="Privacy policy link"
              hint={
                urlOk ? (
                  'A page on your site (/privacy) or a full https:// address.'
                ) : (
                  <span className="text-danger/90">Use a path on your site like /privacy, or an https:// URL.</span>
                )
              }
            >
              <input
                value={draft.privacyUrl}
                onChange={(e) => update({ privacyUrl: e.target.value })}
                maxLength={500}
                spellCheck={false}
                placeholder="/privacy"
                aria-invalid={!urlOk}
                className={classNames('ui-input-mono', !urlOk && '!border-danger/50')}
              />
            </Field>

            <Field label="Position">
              <Select value={draft.position} onChange={(v) => update({ position: v as Position })} label="Position">
                {CONSENT_POSITIONS.map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>

            <div className="space-y-1.5">
              <span className={CAPTION}>Preview</span>
              <BannerPreview preview={preview} />
            </div>
          </>
        )}

        {error && <ErrorBanner message={error} />}

        <div className="flex items-center gap-3">
          <button type="submit" disabled={saving || !urlOk} className="ui-btn-accent !px-4 !py-2 !text-[12px]">
            {saving && <Spinner />}
            {saving ? 'Saving…' : 'Save banner'}
          </button>
          {saved && !dirty && <span className="text-[11px] text-muted">Saved. Publish to apply.</span>}
        </div>
      </form>
    </Section>
  );
}

/**
 * A static sketch of the banner on a page. The real one is a light card in
 * the site's own page (not Dunea's theme), so the sketch is light too.
 */
function BannerPreview({ preview }: { preview: ReturnType<typeof consentPreview> }) {
  return (
    <div
      aria-hidden
      className={classNames(
        'flex h-44 items-end rounded-lg border border-hairline bg-sunken p-2.5',
        preview.align === 'left' && 'justify-start',
        preview.align === 'right' && 'justify-end',
        preview.align === 'center' && 'justify-center',
      )}
    >
      <div
        className={classNames(
          'rounded-md bg-white p-2.5 text-black shadow-[0_6px_24px_rgba(0,0,0,0.25)]',
          preview.align === 'center' ? 'w-full' : 'w-[70%]',
        )}
      >
        <div className="text-[11px] font-semibold">{preview.title}</div>
        <p className="mt-0.5 line-clamp-2 text-[10px] leading-snug text-black/75">
          {preview.message}
          {preview.showPrivacyLink && <span className="underline"> Privacy policy</span>}
        </p>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {preview.buttons.map((b, i) => (
            <span
              key={b}
              className={classNames(
                'rounded border px-1.5 py-0.5 text-[9px]',
                i === preview.buttons.length - 1 ? 'border-black bg-black text-white' : 'border-black/40',
              )}
            >
              {b}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
